/**
 * Plan 12, Task 3.1 — Bridge mesh builder.
 *
 * Bridges use named `boardSchema.bridges[]` entries rather than walk-connection
 * iteration. Endpoints are clamped to measured island render footprints when
 * available, falling back to logical island radii for procedural/debug paths.
 *
 * GLB path: when bridgeGltfScene is supplied, renders default-size visual
 * segments as instanced bridge meshes. No wind-sway shader in GLB path.
 * Procedural fallback: merged BoxGeometry segments + wind-sway ShaderMaterial.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import renderScales from '@shared/render-scales';
import type { ShaderRegistry } from '../shaders/types';
import type { IslandBridgeAttachmentBounds } from './islands';

export interface BridgeDef {
  id: string;
  fromIslandId: string;
  toIslandId: string;
  type: string;
  bridgeMultiplier?: number;
}

export interface IslandDataForBridges {
  centroid: { x: number; y: number; z: number };
  radius: number;
  bridgeAttachmentRadius?: number;
  bridgeAttachmentBounds?: IslandBridgeAttachmentBounds;
  surfaceY: number;
}

export interface BridgeBuildArgs {
  bridges: ReadonlyArray<BridgeDef>;
  islandData: ReadonlyMap<string, IslandDataForBridges>;
  shaderRegistry: ShaderRegistry;
  bridgeGltfScene?: THREE.Group | null;
}

export interface BridgeMeshResult {
  /** All bridge meshes. Add to scene groups.board. */
  readonly meshes: readonly THREE.Mesh[];
  /** Derived bridge local-to-board transforms keyed by bridge id. */
  readonly bridgeTransforms: ReadonlyMap<string, THREE.Matrix4>;
  /** Advance wind-sway uTime on the bridge material. Call per frame. */
  update(deltaSeconds: number): void;
  /** Dispose geometry and material. Idempotent. */
  dispose(): void;
}

export interface BridgeTemplateMetrics {
  readonly lengthZ: number;
  readonly recenterOffset: THREE.Vector3;
}

interface DerivedBridgeSegment {
  bridgeId: string;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
  length: number;
}

interface DerivedBridgePlacement {
  bridgeId: string;
  endA: THREE.Vector3;
  endB: THREE.Vector3;
  visualStart: THREE.Vector3;
  visualEnd: THREE.Vector3;
  direction: THREE.Vector3;
}

const BRIDGE_END_EMBED = 0.08;
const EPSILON = 0.000001;

export function measureBridgeTemplate(
  bridgeGltfScene?: THREE.Object3D | null,
): BridgeTemplateMetrics {
  if (bridgeGltfScene == null) {
    return {
      lengthZ: Math.max(
        renderScales.bridge.minTemplateLengthZ,
        renderScales.bridge.templateLengthZ,
      ),
      recenterOffset: new THREE.Vector3(0, 0, 0),
    };
  }

  const box = new THREE.Box3().setFromObject(bridgeGltfScene);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  return {
    lengthZ: Math.max(renderScales.bridge.minTemplateLengthZ, size.z),
    recenterOffset: new THREE.Vector3(-center.x, -renderScales.bridge.deckAnchorY, -center.z),
  };
}

function distanceToAttachmentBoundsEdge(
  bounds: IslandBridgeAttachmentBounds,
  dirX: number,
  dirZ: number,
): number | null {
  const distances: number[] = [];

  if (dirX > EPSILON) distances.push(bounds.maxX / dirX);
  else if (dirX < -EPSILON) distances.push(bounds.minX / dirX);

  if (dirZ > EPSILON) distances.push(bounds.maxZ / dirZ);
  else if (dirZ < -EPSILON) distances.push(bounds.minZ / dirZ);

  const positiveDistances = distances.filter(
    (distance) => Number.isFinite(distance) && distance >= 0,
  );
  if (positiveDistances.length === 0) return null;

  return Math.min(...positiveDistances);
}

export function getBridgeAttachmentDistance(
  island: IslandDataForBridges,
  dirX: number,
  dirZ: number,
): number {
  if (island.bridgeAttachmentBounds != null) {
    const boundsDistance = distanceToAttachmentBoundsEdge(
      island.bridgeAttachmentBounds,
      dirX,
      dirZ,
    );
    if (boundsDistance != null) return Math.max(0, boundsDistance);
  }

  return Math.max(0, island.bridgeAttachmentRadius ?? island.radius);
}

/**
 * Build bridge geometry for all named bridges in boardSchema.bridges[].
 *
 * GLB path (when bridgeGltfScene is provided):
 *   - Measures the bridge GLB length and renders unscaled visual segments.
 *   - Endpoints use measured island attachment bounds when present.
 *   - Adds a small end embed so segments hide rim seams.
 *   - Returns a Group of instanced meshes. No wind-sway shader.
 *
 * Procedural fallback (when bridgeGltfScene is null/undefined):
 *   - Merged render-scales.json-sized BoxGeometry segments, wind-sway shader.
 *   - Bridges with missing island data are skipped (warning logged).
 *
 * When there are no named bridges, returns an empty meshes array.
 */
export function buildBridges(args: BridgeBuildArgs): BridgeMeshResult {
  const { bridges, islandData, shaderRegistry, bridgeGltfScene } = args;

  if (bridges.length === 0) {
    return { meshes: [], bridgeTransforms: new Map(), update() {}, dispose() {} };
  }

  // ── Compute bridge segments from named bridges ────────────────────────────
  const segments: DerivedBridgeSegment[] = [];
  const placements: DerivedBridgePlacement[] = [];
  const bridgeTransforms = new Map<string, THREE.Matrix4>();
  const bridgeTemplateMetrics = measureBridgeTemplate(bridgeGltfScene);
  const bridgeTemplateLengthZ = bridgeTemplateMetrics.lengthZ;
  const tempObj = new THREE.Object3D();
  const up = new THREE.Vector3(0, 1, 0);

  for (const bridge of bridges) {
    const fromIsland = islandData.get(bridge.fromIslandId);
    const toIsland = islandData.get(bridge.toIslandId);

    if (!fromIsland || !toIsland) {
      console.warn(
        `[buildBridges] Bridge "${bridge.id}" references unknown island(s): ` +
          `fromIslandId="${bridge.fromIslandId}" (${fromIsland ? 'found' : 'MISSING'}), ` +
          `toIslandId="${bridge.toIslandId}" (${toIsland ? 'found' : 'MISSING'}). Skipping.`,
      );
      continue;
    }

    const fc = fromIsland.centroid;
    const tc = toIsland.centroid;

    // Direction vector (XZ only — ignore Y for orientation)
    const dirX = tc.x - fc.x;
    const dirZ = tc.z - fc.z;
    const horizDist = Math.sqrt(dirX * dirX + dirZ * dirZ);
    const hasCentroidDirection = horizDist >= 0.001;
    const ndx = hasCentroidDirection ? dirX / horizDist : 1;
    const ndz = hasCentroidDirection ? dirZ / horizDist : 0;

    const fromRadius = getBridgeAttachmentDistance(fromIsland, ndx, ndz);
    const toRadius = getBridgeAttachmentDistance(toIsland, -ndx, -ndz);

    // Endpoint A: fromCentroid + dir * visual attachment radius, Y = fromSurfaceY
    const endA = new THREE.Vector3(
      fc.x + ndx * fromRadius,
      fromIsland.surfaceY,
      fc.z + ndz * fromRadius,
    );

    // Endpoint B: toCentroid - dir * visual attachment radius, Y = toSurfaceY
    const endB = new THREE.Vector3(tc.x - ndx * toRadius, toIsland.surfaceY, tc.z - ndz * toRadius);

    const centerline = new THREE.Vector3().subVectors(endB, endA);
    const edgeDistance = centerline.length();
    const direction =
      edgeDistance > 0.001
        ? centerline.clone().divideScalar(edgeDistance)
        : new THREE.Vector3(ndx, 0, ndz);
    const logicalLength = Math.max(renderScales.bridge.minVisibleLength, edgeDistance);
    const logicalPosition = new THREE.Vector3().addVectors(endA, endB).multiplyScalar(0.5);
    tempObj.position.copy(logicalPosition);
    tempObj.up.copy(up);
    tempObj.lookAt(logicalPosition.clone().add(direction));
    const logicalQuaternion = tempObj.quaternion.clone();
    const logicalScale = new THREE.Vector3(1, 1, logicalLength / bridgeTemplateLengthZ);
    bridgeTransforms.set(
      bridge.id,
      new THREE.Matrix4().compose(logicalPosition, logicalQuaternion, logicalScale),
    );

    const visualStart = endA.clone().addScaledVector(direction, -BRIDGE_END_EMBED);
    const visualEnd = endB.clone().addScaledVector(direction, BRIDGE_END_EMBED);
    placements.push({
      bridgeId: bridge.id,
      endA: endA.clone(),
      endB: endB.clone(),
      visualStart: visualStart.clone(),
      visualEnd: visualEnd.clone(),
      direction: direction.clone(),
    });
    const visualSpan = visualStart.distanceTo(visualEnd);
    const minSegments = Math.max(1, Math.ceil(visualSpan / bridgeTemplateLengthZ));
    const requestedSegments =
      typeof bridge.bridgeMultiplier === 'number' && Number.isFinite(bridge.bridgeMultiplier)
        ? Math.ceil(bridge.bridgeMultiplier)
        : minSegments;
    const segmentCount = Math.max(minSegments, requestedSegments);
    const segmentSpacing =
      segmentCount <= 1 || visualSpan <= bridgeTemplateLengthZ
        ? 0
        : (visualSpan - bridgeTemplateLengthZ) / (segmentCount - 1);

    for (let i = 0; i < segmentCount; i++) {
      const position =
        segmentCount <= 1 || visualSpan <= bridgeTemplateLengthZ
          ? new THREE.Vector3().addVectors(visualStart, visualEnd).multiplyScalar(0.5)
          : visualStart
              .clone()
              .addScaledVector(direction, bridgeTemplateLengthZ / 2 + segmentSpacing * i);
      tempObj.position.copy(position);
      tempObj.up.copy(up);
      tempObj.lookAt(position.clone().add(direction));
      segments.push({
        bridgeId: bridge.id,
        position,
        quaternion: tempObj.quaternion.clone(),
        scale: new THREE.Vector3(1, 1, 1),
        length: bridgeTemplateLengthZ,
      });
    }
  }

  if (segments.length === 0) {
    return { meshes: [], bridgeTransforms, update() {}, dispose() {} };
  }

  // ── GLB path ──────────────────────────────────────────────────────────────
  if (bridgeGltfScene != null) {
    const bridgesGroup = new THREE.Group();
    bridgesGroup.name = 'bridges';
    bridgesGroup.userData.segmentCount = segments.length;
    bridgesGroup.userData.bridgeSegmentCounts = countSegmentsByBridgeId(segments);
    bridgesGroup.userData.bridgeSegments = segments.map((segment) => ({
      bridgeId: segment.bridgeId,
      position: segment.position.toArray(),
      quaternion: segment.quaternion.toArray(),
      length: segment.length,
    }));
    bridgesGroup.userData.bridgePlacements = placements.map((placement) => ({
      bridgeId: placement.bridgeId,
      endA: placement.endA.toArray(),
      endB: placement.endB.toArray(),
      visualStart: placement.visualStart.toArray(),
      visualEnd: placement.visualEnd.toArray(),
      direction: placement.direction.toArray(),
    }));

    bridgeGltfScene.updateMatrixWorld(true);
    const templateMeshes: THREE.Mesh[] = [];
    bridgeGltfScene.traverse((child) => {
      if (child instanceof THREE.Mesh) templateMeshes.push(child);
    });

    const recenterMatrix = new THREE.Matrix4().makeTranslation(
      bridgeTemplateMetrics.recenterOffset.x,
      bridgeTemplateMetrics.recenterOffset.y,
      bridgeTemplateMetrics.recenterOffset.z,
    );
    const segmentMatrix = new THREE.Matrix4();
    const instanceMatrix = new THREE.Matrix4();

    for (const templateMesh of templateMeshes) {
      const instanced = new THREE.InstancedMesh(
        templateMesh.geometry,
        templateMesh.material,
        segments.length,
      );
      instanced.name = `bridge-segments:${templateMesh.name || 'mesh'}`;
      instanced.userData.isBridge = true;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i]!;
        segmentMatrix.compose(segment.position, segment.quaternion, segment.scale);
        instanceMatrix
          .copy(segmentMatrix)
          .multiply(recenterMatrix)
          .multiply(templateMesh.matrixWorld);
        instanced.setMatrixAt(i, instanceMatrix);
      }
      instanced.instanceMatrix.needsUpdate = true;
      bridgesGroup.add(instanced);
    }

    let glbDisposed = false;
    function disposeGlb(): void {
      if (glbDisposed) return;
      glbDisposed = true;
    }

    return {
      meshes: [bridgesGroup as unknown as THREE.Mesh],
      bridgeTransforms,
      update() {},
      dispose: disposeGlb,
    };
  }

  // ── Procedural fallback ────────────────────────────────────────────────────
  // One baked BoxGeometry per bridge segment, merged into a single mesh so
  // segmenting does not multiply draw calls on the procedural fallback path.
  const factory = shaderRegistry.get('wind-sway');
  const geometries: THREE.BufferGeometry[] = [];
  let sharedMaterial: ReturnType<typeof factory> | null = null;

  // All procedural bridges share one wind-sway material for efficiency.
  sharedMaterial = factory();
  sharedMaterial.uniforms['uBaseColor']!.value = new THREE.Color(0x6b4423);
  sharedMaterial.uniforms['uIntensity']!.value = 0.05;
  sharedMaterial.uniforms['uStiffness']!.value = 2.0;

  const tempObjProc = new THREE.Object3D();

  for (const segment of segments) {
    const geo = new THREE.BoxGeometry(
      renderScales.bridge.proceduralWidth,
      renderScales.bridge.proceduralHeight,
      bridgeTemplateLengthZ,
    );
    tempObjProc.position.copy(segment.position);
    tempObjProc.quaternion.copy(segment.quaternion);
    tempObjProc.updateMatrix();
    geo.applyMatrix4(tempObjProc.matrix);
    geometries.push(geo);
  }

  const mergedGeometry = mergeGeometries(geometries, false);
  for (const g of geometries) g.dispose();
  if (mergedGeometry == null) {
    sharedMaterial.dispose();
    throw new Error('buildBridges: failed to merge procedural bridge segment geometry');
  }

  const mesh = new THREE.Mesh(mergedGeometry, sharedMaterial);
  mesh.name = 'bridge-segments';
  mesh.userData.segmentCount = segments.length;
  mesh.userData.bridgeSegmentCounts = countSegmentsByBridgeId(segments);
  mesh.userData.bridgeTemplateLengthZ = bridgeTemplateLengthZ;
  mesh.userData.bridgePlacements = placements.map((placement) => ({
    bridgeId: placement.bridgeId,
    endA: placement.endA.toArray(),
    endB: placement.endB.toArray(),
    visualStart: placement.visualStart.toArray(),
    visualEnd: placement.visualEnd.toArray(),
    direction: placement.direction.toArray(),
  }));
  const meshes: THREE.Mesh[] = [mesh];

  let disposed = false;

  function update(deltaSeconds: number): void {
    if (sharedMaterial) {
      sharedMaterial.uniforms['uTime']!.value += deltaSeconds;
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    mergedGeometry.dispose();
    sharedMaterial?.dispose();
  }

  return { meshes, bridgeTransforms, update, dispose };
}

function countSegmentsByBridgeId(
  segments: readonly DerivedBridgeSegment[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const segment of segments) {
    counts[segment.bridgeId] = (counts[segment.bridgeId] ?? 0) + 1;
  }
  return counts;
}
